import { BarChart3, Database, Percent, Search } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";
import { Skeleton } from "@/common/ui/skeleton";

import type { RagQualityStats } from "./rag-quality-types";

interface RagStatsCardsProps {
  stats: RagQualityStats | null;
  isLoading: boolean;
}

interface StatCardConfig {
  title: string;
  key: keyof RagQualityStats;
  icon: React.ReactNode;
  format: (value: number) => string;
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function formatScore(value: number): string {
  return value.toFixed(2);
}

function formatCount(value: number): string {
  return value.toLocaleString();
}

const STAT_CARDS: StatCardConfig[] = [
  {
    title: "Citation Rate",
    key: "citation_rate",
    icon: <Percent className="h-4 w-4 text-muted-foreground" />,
    format: formatPercent,
  },
  {
    title: "Avg Relevance Score",
    key: "avg_relevance_score",
    icon: <BarChart3 className="h-4 w-4 text-muted-foreground" />,
    format: formatScore,
  },
  {
    title: "Queries Today",
    key: "queries_today",
    icon: <Search className="h-4 w-4 text-muted-foreground" />,
    format: formatCount,
  },
  {
    title: "Index Documents",
    key: "index_document_count",
    icon: <Database className="h-4 w-4 text-muted-foreground" />,
    format: formatCount,
  },
];

export function RagStatsCards({ stats, isLoading }: RagStatsCardsProps): React.ReactElement {
  return (
    <div
      data-testid="rag-stats-cards"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      {STAT_CARDS.map((card) => (
        <Card key={card.key}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
            {card.icon}
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton data-testid={`rag-stat-skeleton-${card.key}`} className="h-8 w-20" />
            ) : (
              <div data-testid={`rag-stat-${card.key}`} className="text-2xl font-bold">
                {stats ? card.format(stats[card.key]) : "\u2014"}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
